// SUB-OFFICE 7 — the Bureau voice accessor.
// Every line the world speaks routes through here, sourced from the shipped
// corpus (src/content/voice.json, import-only) plus canonical spec lines.
// Rules enforced at the accessor: no-repeat LRUs; closers frequency-budgeted
// (never the same closer twice in a row). No exclamation marks exist in the
// corpus; nothing here says the words the Bureau does not say.

import voice from '../content/voice.json';
import { LruPick } from './rng';

export const POSTERS: readonly string[] = voice.posters;
export const PLAQUE: string = voice.plaque;
export const HOLD_LINES: readonly string[] = voice.holdLines;
export const CONFER_LINES: readonly string[] = voice.conferLines;
export const NOT_FOUND_LINES: readonly string[] = voice.notFoundLines;
export const DISTRESS_LINE: string = voice.distressLine;
export const SEALED_LINE: string = voice.sealedLine;
export const INDUSTRIAL_ACTION_LINES: readonly string[] = voice.industrialAction;
export const GRADE_NAMES: readonly string[] = voice.gradeNames;
export const STAMP_FLAVOR: Readonly<Record<string, string>> = voice.stampFlavor;

/** Canonical ambient chyron pronouncements (fiction-bible canon + posters). */
const CHYRON_IDLE: readonly string[] = [
  ...voice.posters,
  'YOUR FILE EXISTS. DO NOT MISPLACE YOURSELF.',
  'THE QUEUE IS MOVING. MOVEMENT IS NOT PROGRESS. THE BUREAU TRACKS BOTH.',
  'ALL WINDOWS ARE STAFFED. STAFFING IS A STATE OF MIND.',
  'FORMS ARE AVAILABLE. AVAILABILITY IS NOT AN INVITATION. IT IS A TEST.',
  'THE ADJUDICATION VAULT REMAINS SEALED. THIS IS FOR EVERYONE.',
  'DEPARTMENT 6 HAS NO COMMENT.',
  'STANDING IS RECALCULATED CONTINUOUSLY. STAND ACCORDINGLY.',
  'THE BUREAU IS OPEN. THE BUREAU WAS NEVER CLOSED.',
];

/** Locked-door lines (canonical). */
export const DOOR_NO_DEPARTMENT = 'THIS DOOR HAS NO DEPARTMENT.';
export const DOOR_MEZZANINE = 'STAIRWELL PENDING AUTHORIZATION.';
export const DOOR_SEALED = 'SEALED BY ORDER OF DEPARTMENT 2.';

const idlePicker = new LruPick(CHYRON_IDLE, Math.min(20, CHYRON_IDLE.length - 1));
const holdPicker = new LruPick(HOLD_LINES, 4);
const notFoundPicker = new LruPick(NOT_FOUND_LINES, 2);

export function nextIdleLine(): string {
  return idlePicker.next();
}
export function nextHoldLine(): string {
  return holdPicker.next();
}
export function nextNotFoundLine(): string {
  return notFoundPicker.next();
}

// ---------------------------------------------------------------------------
// Closers — frequency-budgeted (LRU; a closer never repeats back-to-back)
// ---------------------------------------------------------------------------

const CLOSERS = ['This is noted.', 'This is reflected in your file.', 'Proceed.'] as const;
const closerPicker = new LruPick<string>(CLOSERS, 1);

/** Append a budgeted closer to a memo-like line. */
export function withCloser(line: string): string {
  return line.endsWith('.') ? `${line} ${closerPicker.next()}` : `${line}. ${closerPicker.next()}`;
}

/**
 * Copy-layer law: the Bureau never says these words on any surface.
 * Exposed for debug assertions only (never shipped UI).
 */
export function violatesVoice(text: string): boolean {
  return /!|(\berror\b)|(\bloading\b)|(\bAI\b)|(\bgame\b)|(\bmodel\b)/i.test(text);
}
